import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function SaveToWorkspaceModal({ open, onOpenChange, result, context }) {
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [folderId, setFolderId] = useState("");
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [newCollection, setNewCollection] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/workspace/overview").then((r) => setCollections(r.data.collections || [])).catch(() => {});
    setName(`AI Pro Flyer — ${new Date().toLocaleDateString()}`);
    setFolderId("");
  }, [open]);

  const folders = (collections.find((c) => c.id === collectionId)?.folders || []).filter((f) => !f.archived);

  const save = async () => {
    if (!name.trim()) { toast.error("Give your project a name."); return; }
    setSaving(true);
    try {
      let colId = collectionId || null;
      if (newCollection.trim()) {
        const r = await api.post("/workspace/collections", { name: newCollection.trim() });
        colId = r.data.id;
      }
      await api.post("/workspace/projects", {
        name: name.trim(),
        image_storage_path: result?.storage_path || null,
        event_details: context?.eventDetails || "",
        custom_instructions: context?.direction || "",
        category_id: context?.director?.category_id || null,
        subcategory_id: context?.director?.subcategory_id || null,
        director_id: context?.director?.id || null,
        collection_id: colId,
        folder_id: colId && colId === collectionId ? folderId || null : null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        settings: {
          aspect_ratio: context?.aspect,
          quality_tier: context?.tier,
          generation_mode: context?.generationMode,
          width: result?.width,
          height: result?.height,
        },
      });
      toast.success("Saved to your Workspace.");
      onOpenChange(false);
      setNewCollection("");
      setTags("");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save the project."));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-amber-400/60";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="save-workspace-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Save to Workspace</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Project name</label>
            <input data-testid="ws-save-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Collection <span className="text-zinc-600 normal-case font-normal">· optional</span></label>
            <select data-testid="ws-save-collection" value={collectionId} onChange={(e) => { setCollectionId(e.target.value); setFolderId(""); }} className={inputCls}>
              <option value="">Unfiled</option>
              {collections.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {collectionId && folders.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Folder</label>
              <select data-testid="ws-save-folder" value={folderId} onChange={(e) => setFolderId(e.target.value)} className={inputCls}>
                <option value="">No folder</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Or create a new collection</label>
            <input data-testid="ws-save-new-collection" value={newCollection} onChange={(e) => setNewCollection(e.target.value)} placeholder="e.g. Client — Marcus Events" className={inputCls} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Tags <span className="text-zinc-600 normal-case font-normal">· comma separated</span></label>
            <input data-testid="ws-save-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Client Approved, Detroit, Gold Palette" className={inputCls} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="ws-save-submit" onClick={save} disabled={saving} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">
            {saving ? "Saving…" : "Save project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
